import { useEffect, useState } from "react";
import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTenants } from "@/api/queries";
import {
  getTargetTenant,
  setTargetTenant,
  subscribeTargetTenant,
} from "@/state/targetTenant";
import { cn } from "@/lib/cn";

export function TenantSwitcher() {
  const { data, isLoading } = useTenants();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(getTargetTenant());

  useEffect(() => {
    return subscribeTargetTenant(setSelected);
  }, []);

  // Авто-выбор первого tenant'а если ничего не выбрано.
  useEffect(() => {
    if (!selected && data?.items && data.items.length > 0) {
      setTargetTenant(data.items[0].slug);
    }
  }, [data, selected]);

  const pick = (slug: string) => {
    setTargetTenant(slug);
    setOpen(false);
    // Инвалидируем весь кеш — данные scope-нуты к tenant'у.
    qc.invalidateQueries();
  };

  const currentTenant = data?.items.find((t) => t.slug === selected);
  const label = isLoading
    ? "…"
    : data?.items.length === 0
      ? "Нет организаций"
      : (currentTenant?.name ?? currentTenant?.slug ?? "Выберите организацию");

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isLoading || !data?.items.length}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm",
          "bg-white/[0.03] hover:bg-white/[0.06] border border-border",
          "text-left disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        <Building2 size={14} className="text-fg-subtle shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-fg-subtle">
            Организация
          </div>
          <div className="truncate font-medium">{label}</div>
        </div>
        <ChevronsUpDown size={14} className="text-fg-subtle shrink-0" />
      </button>

      {open && data?.items && data.items.length > 0 && (
        <div
          className="absolute z-20 mt-1 left-0 right-0 rounded-lg bg-bg-surface border border-border shadow-xl max-h-64 overflow-y-auto"
          onMouseLeave={() => setOpen(false)}
        >
          {data.items.map((t) => {
            const active = t.slug === selected;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => pick(t.slug)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left",
                  "hover:bg-white/5 transition-colors",
                  active && "bg-accent-subtle text-accent",
                )}
              >
                <Check
                  size={14}
                  className={cn(active ? "opacity-100" : "opacity-0")}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{t.name}</div>
                  <div className="truncate text-xs text-fg-subtle">
                    {t.slug}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
